import React, { useState } from 'react';
import { Users, DollarSign, CheckCircle, ChevronRight, ChevronDown } from 'lucide-react';
import { FinancialValue } from '../shared/FinancialValue';
import { formatCurrency } from '../../utils/financialFormat';

// Box definitions — order, labels, and whether each contributes to
// non-operating income on the revenue reconciliation.  Box 1 stays as the
// primary "Officer Compensation" column; the other 9 expand on click.
interface K1BoxDef {
  key: string;
  label: string;
  shortLabel: string;
  nonOperating: boolean;
}

const K1_BOX_DEFS: K1BoxDef[] = [
  { key: 'box_1_ordinary_income',           label: 'Box 1: Ordinary Business Income',      shortLabel: 'Box 1 Ord. Income',  nonOperating: false },
  { key: 'box_2_net_rental_real_estate',    label: 'Box 2: Net Rental Real Estate',        shortLabel: 'Box 2 Rental RE',    nonOperating: true  },
  { key: 'box_3_other_rental_income',       label: 'Box 3: Other Rental Income',           shortLabel: 'Box 3 Other Rental', nonOperating: true  },
  { key: 'box_4_guaranteed_payments',       label: 'Box 4: Guaranteed Payments',           shortLabel: 'Box 4 Guaranteed',   nonOperating: false },
  { key: 'box_5_interest',                  label: 'Box 5: Interest Income',               shortLabel: 'Box 5 Interest',     nonOperating: true  },
  { key: 'box_6_dividends',                 label: 'Box 6: Ordinary Dividends',            shortLabel: 'Box 6 Dividends',    nonOperating: true  },
  { key: 'box_7_royalties',                 label: 'Box 7: Royalties',                     shortLabel: 'Box 7 Royalties',    nonOperating: true  },
  { key: 'box_8_net_st_capital_gain',       label: 'Box 8: Net ST Capital Gain',           shortLabel: 'Box 8 ST Cap Gain',  nonOperating: true  },
  { key: 'box_9a_net_lt_capital_gain',      label: 'Box 9a: Net LT Capital Gain',          shortLabel: 'Box 9a LT Cap Gain', nonOperating: true  },
  { key: 'box_11_other_income',             label: 'Box 11: Other Income',                 shortLabel: 'Box 11 Other Inc.',  nonOperating: true  },
  { key: 'box_14_self_employment_earnings', label: 'Box 14: Self-Employment Earnings',     shortLabel: 'Box 14 SE',          nonOperating: false },
  { key: 'box_19_distributions',            label: 'Box 19: Distributions',                shortLabel: 'Box 19 Distrib.',    nonOperating: false },
];

interface K1Partner {
  name: string;
  box_1_ordinary_income?: number;
  box_2_net_rental_real_estate?: number;
  box_3_other_rental_income?: number;
  box_4_guaranteed_payments?: number;
  box_5_interest?: number;
  box_6_dividends?: number;
  box_7_royalties?: number;
  box_8_net_st_capital_gain?: number;
  box_9a_net_lt_capital_gain?: number;
  box_11_other_income?: number;
  box_14_self_employment_earnings?: number;
  box_19_distributions?: number;
}

interface K1Data {
  filename: string;
  year: string;
  form_type?: string;
  data: {
    form_type?: string;
    document_type?: string;
    tax_year?: string;
    partner_name?: string;
    // Box fields at entry level (how claim_service propagates them post-expansion)
    box_1_ordinary_income?: number;
    box_2_net_rental_real_estate?: number;
    box_3_other_rental_income?: number;
    box_4_guaranteed_payments?: number;
    box_5_interest?: number;
    box_6_dividends?: number;
    box_7_royalties?: number;
    box_8_net_st_capital_gain?: number;
    box_9a_net_lt_capital_gain?: number;
    box_11_other_income?: number;
    box_14_self_employment_earnings?: number;
    box_19_distributions?: number;
    // K-1 data block (older shape)
    k1_data?: Partial<K1Partner>;
    schedule_k1?: {
      total_officer_compensation?: number;
      partners?: K1Partner[];
    };
    schedule_k?: {
      line_1_ordinary_income?: number;
    };
  };
}

interface K1PartnerSummaryProps {
  tax1065Files: K1Data[];
}

// Per partner, per year, per box value
type BoxTotals = Record<string, number>; // box_key -> amount
type PartnerYearMatrix = Record<string, Record<string, BoxTotals>>; // partner -> year -> box_key -> amount

export default function K1PartnerSummary({ tax1065Files }: K1PartnerSummaryProps) {
  const [expandedPartners, setExpandedPartners] = useState<Set<string>>(new Set());

  const toggle = (p: string) => {
    setExpandedPartners(prev => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });
  };

  // Collect boxes for a partner within a single K-1 file.  Values come from
  // the nested schedule_k1.partners[] entry AND from the flat entry-level
  // box fields (post-expansion claim_service propagates both shapes).
  const collectBoxes = (partner: Partial<K1Partner>, entryData: K1Data['data']): BoxTotals => {
    const out: BoxTotals = {};
    for (const def of K1_BOX_DEFS) {
      const pv = (partner as any)[def.key];
      const ev = (entryData as any)[def.key];
      const kv = (entryData.k1_data as any)?.[def.key];
      // Prefer partner-specific value, then k1_data sub-block, then entry-level
      const v = pv ?? kv ?? ev;
      if (typeof v === 'number' && !Number.isNaN(v)) {
        out[def.key] = v;
      }
    }
    return out;
  };

  // Aggregate data
  const aggregate = () => {
    const matrix: PartnerYearMatrix = {};
    const yearTotalsByBox: Record<string, BoxTotals> = {}; // year -> box_key -> sum
    const allYears = new Set<string>();

    for (const file of tax1065Files) {
      const data = file.data || {};
      const year = (data.tax_year || file.year || '').toString();
      if (!year) continue;

      const formType = (file.form_type || data.form_type || data.document_type || '').toString();
      const isK1 = formType === 'K-1' || formType === 'K1' || formType.toLowerCase() === 'k-1';
      const is1065WithK1Data = formType === '1065' && data.schedule_k1 && (
        (data.schedule_k1.partners?.length ?? 0) > 0 ||
        (data.schedule_k1.total_officer_compensation ?? 0) > 0
      );
      if (!(isK1 || is1065WithK1Data) || !data.schedule_k1) continue;

      const partners = data.schedule_k1.partners;
      const partnerList: Array<{ name: string; boxes: BoxTotals }> = [];

      if (partners && Array.isArray(partners) && partners.length > 0) {
        for (const p of partners) {
          partnerList.push({
            name: p.name || 'Unknown Partner',
            boxes: collectBoxes(p, data),
          });
        }
      } else if (data.partner_name) {
        // Single-partner K-1 with explicit partner_name
        partnerList.push({
          name: data.partner_name,
          boxes: collectBoxes({ name: data.partner_name }, data),
        });
      }

      for (const { name, boxes } of partnerList) {
        if (!matrix[name]) matrix[name] = {};
        if (!matrix[name][year]) matrix[name][year] = {};
        for (const [boxKey, amt] of Object.entries(boxes)) {
          // Don't double-count if this partner/year/box already has a value
          // (a second K-1 file for the same partner/year shouldn't overwrite).
          if (matrix[name][year][boxKey] === undefined) {
            matrix[name][year][boxKey] = amt;
            if (!yearTotalsByBox[year]) yearTotalsByBox[year] = {};
            yearTotalsByBox[year][boxKey] = (yearTotalsByBox[year][boxKey] ?? 0) + amt;
          }
        }
        allYears.add(year);
      }
    }

    // Remove "Unknown Partner" entries if named partners exist (it usually
    // represents the 1065 combined total — kept for verification earlier but
    // now just noise).
    const form1065Totals: Record<string, BoxTotals> = {};
    let has1065Verification = false;
    const namedPartners = Object.keys(matrix).filter(n => n !== 'Unknown Partner');
    if (namedPartners.length > 0 && matrix['Unknown Partner']) {
      has1065Verification = true;
      for (const [y, boxes] of Object.entries(matrix['Unknown Partner'])) {
        form1065Totals[y] = boxes;
        // Subtract from year totals
        for (const [bk, amt] of Object.entries(boxes)) {
          if (yearTotalsByBox[y]?.[bk] !== undefined) {
            yearTotalsByBox[y][bk] -= amt;
          }
        }
      }
      delete matrix['Unknown Partner'];
    }

    const years = Array.from(allYears).sort();
    const partnerNames = Object.keys(matrix).sort();
    return { matrix, yearTotalsByBox, years, partnerNames, form1065Totals, has1065Verification };
  };

  const { matrix, yearTotalsByBox, years, partnerNames, form1065Totals, has1065Verification } = aggregate();

  // Grand totals for Box 1 (primary) and for NON-OPERATING aggregate
  const grandBox1 = partnerNames.reduce((sum, p) =>
    sum + years.reduce((s, y) => s + (matrix[p][y]?.box_1_ordinary_income ?? 0), 0), 0);
  const form1065Box1Total = Object.values(form1065Totals).reduce(
    (sum, yrBoxes) => sum + (yrBoxes.box_1_ordinary_income ?? 0), 0
  );
  const verificationPassed = has1065Verification && Math.abs(grandBox1 - form1065Box1Total) < 1;
  const verificationDiff = has1065Verification ? grandBox1 - form1065Box1Total : 0;

  // Per-year NON-OPERATING income aggregate (the reconciliation-relevant number)
  const nonOperatingBoxKeys = K1_BOX_DEFS.filter(d => d.nonOperating).map(d => d.key);
  const nonOperatingByYear: Record<string, number> = {};
  for (const y of years) {
    let s = 0;
    for (const bk of nonOperatingBoxKeys) s += yearTotalsByBox[y]?.[bk] ?? 0;
    nonOperatingByYear[y] = s;
  }
  const totalNonOperating = Object.values(nonOperatingByYear).reduce((sum, v) => sum + v, 0);

  if (partnerNames.length === 0) {
    return (
      <div
        className="rounded-lg p-6"
        style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}
      >
        <p className="cs-body mb-2" style={{ color: 'var(--color-needs-human-text)', fontWeight: 600 }}>No K-1 Partner Data Available</p>
        <p className="cs-meta" style={{ color: 'var(--color-needs-human-text)' }}>
          Upload Form 1065 returns with Schedule K-1 partner sections, or individual K-1 PDFs.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="cs-h2 flex items-center gap-2">
            <Users className="w-6 h-6" style={{ color: 'var(--color-brand)' }} />
            K-1 Partner Summary
          </h3>
          <p className="cs-meta mt-1">
            Box 1 by default — click any partner row to expand and see all 10 reportable boxes.
            Non-operating income (rental, interest, dividends, royalties, capital gains) is tracked
            per-year so the reconciliation view can subtract it from bank deposits when computing
            the 1065 gross-receipts coverage gap.
          </p>
        </div>
      </div>

      {/* 1065 Verification Banner (Box 1 only — that's the reconciled anchor) */}
      {has1065Verification && (
        <div
          className="rounded-lg p-4"
          style={
            verificationPassed
              ? { background: 'transparent', border: '1px solid var(--color-verified)' }
              : { background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }
          }
        >
          <div className="flex items-center gap-3">
            {verificationPassed
              ? <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
              : <DollarSign className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />}
            <div className="flex-1">
              <p className="cs-body" style={{ color: verificationPassed ? 'var(--color-verified)' : 'var(--color-needs-human-text)', fontWeight: 600 }}>
                {verificationPassed ? 'Box 1 Reconciled with 1065' : 'Box 1 vs 1065 Mismatch'}
              </p>
              <p className="cs-meta" style={{ color: verificationPassed ? 'var(--color-verified)' : 'var(--color-needs-human-text)' }}>
                1065 Box 1 Total: <FinancialValue value={form1065Box1Total} /> · K-1 Partners Sum: <FinancialValue value={grandBox1} />
                {!verificationPassed && ` · Diff: ${formatCurrency(Math.abs(verificationDiff))}`}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Non-Operating Income Banner (the reconciliation link) */}
      {totalNonOperating > 0 && (
        <div
          className="rounded-lg p-4"
          style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}
        >
          <p className="cs-body mb-1" style={{ color: 'var(--color-needs-human-text)', fontWeight: 600 }}>Non-Operating Income Flows to Bank</p>
          <p className="cs-meta" style={{ color: 'var(--color-needs-human-text)' }}>
            Across all partners and years, non-operating K-1 income (rental + interest + dividends + royalties +
            capital gains + other) sums to <strong><FinancialValue value={totalNonOperating} /></strong>. This total hits
            the partnership's bank account but is NOT in 1065 Line 1a gross receipts — the reconciliation view
            subtracts it before flagging a missing-merchant gap.
          </p>
        </div>
      )}

      {/* Partner × Year Table with expandable rows */}
      <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--color-border)' }}>
        <table className="w-full">
          <thead style={{ background: 'var(--color-bg-elevated)' }}>
            <tr>
              <th className="cs-label text-left px-3 py-3" style={{ width: '1%' }}></th>
              <th className="cs-label text-left px-4 py-3">Partner / Box</th>
              {years.map(year => (
                <th key={year} className="cs-label text-right px-4 py-3">{year}</th>
              ))}
              <th className="cs-label text-right px-4 py-3" style={{ color: 'var(--color-needs-human-text)', background: 'var(--color-needs-human-tint)' }}>Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--color-border-faint)]">
            {partnerNames.map(partner => {
              const isOpen = expandedPartners.has(partner);
              const partnerBox1Total = years.reduce((s, y) => s + (matrix[partner][y]?.box_1_ordinary_income ?? 0), 0);
              return (
                <React.Fragment key={partner}>
                  {/* Header row — Box 1 (default visible) */}
                  <tr
                    className="cursor-pointer transition-colors"
                    onClick={() => toggle(partner)}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <td className="px-3 py-3" style={{ color: 'var(--color-text-muted)' }}>
                      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </td>
                    <td className="cs-body px-4 py-3" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>
                      {partner}
                      <span className="cs-meta ml-2">(Box 1 — click to expand)</span>
                    </td>
                    {years.map(year => (
                      <td key={year} className="cs-figure text-right px-4 py-3">
                        <FinancialValue value={matrix[partner][year]?.box_1_ordinary_income} />
                      </td>
                    ))}
                    <td className="cs-figure text-right px-4 py-3" style={{ color: 'var(--color-needs-human-text)', fontWeight: 600, background: 'var(--color-needs-human-tint)' }}>
                      <FinancialValue value={partnerBox1Total} />
                    </td>
                  </tr>
                  {/* Expanded per-box rows (all boxes except Box 1 since that's the header) */}
                  {isOpen && K1_BOX_DEFS.filter(d => d.key !== 'box_1_ordinary_income').map(def => {
                    const rowTotal = years.reduce((s, y) => s + (matrix[partner][y]?.[def.key] ?? 0), 0);
                    // Hide rows where EVERY year is zero to reduce noise
                    if (rowTotal === 0 && years.every(y => (matrix[partner][y]?.[def.key] ?? 0) === 0)) return null;
                    return (
                      <tr key={`${partner}:${def.key}`} style={{ background: def.nonOperating ? 'var(--color-needs-human-tint)' : 'var(--color-bg-base)' }}>
                        <td className="px-3 py-2"></td>
                        <td className="cs-meta px-4 py-2 pl-10">
                          {def.shortLabel}
                          {def.nonOperating && (
                            <span className="ml-2 text-[10px] uppercase tracking-wide font-semibold" style={{ color: 'var(--color-needs-human-text)' }}>Non-Op</span>
                          )}
                        </td>
                        {years.map(year => (
                          <td key={year} className="cs-figure text-right px-4 py-2">
                            <FinancialValue value={matrix[partner][year]?.[def.key]} />
                          </td>
                        ))}
                        <td className="cs-figure text-right px-4 py-2" style={{ background: 'var(--color-needs-human-tint)' }}>
                          <FinancialValue value={rowTotal} />
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}

            {/* Year totals row — Box 1 only (so the default collapsed view totals) */}
            <tr style={{ background: 'var(--color-needs-human-tint)', fontWeight: 700 }}>
              <td className="px-3 py-3"></td>
              <td className="px-4 py-3" style={{ color: 'var(--color-needs-human-text)' }}>Box 1 Total (per year)</td>
              {years.map(year => (
                <td key={year} className="cs-figure text-right px-4 py-3" style={{ color: 'var(--color-needs-human-text)' }}>
                  <FinancialValue value={yearTotalsByBox[year]?.box_1_ordinary_income} />
                </td>
              ))}
              <td className="cs-figure text-right px-4 py-3 text-lg" style={{ color: 'var(--color-needs-human-text)', background: 'var(--color-needs-human-tint)' }}>
                <FinancialValue value={grandBox1} />
              </td>
            </tr>
            {/* Non-operating total — the reconciliation-relevant number */}
            <tr style={{ background: 'var(--color-bg-elevated)', fontWeight: 600 }}>
              <td className="px-3 py-3"></td>
              <td className="px-4 py-3" style={{ color: 'var(--color-needs-human-text)' }}>
                Non-Operating Total (Boxes 2, 3, 5, 6, 7, 8, 9a, 11)
              </td>
              {years.map(year => (
                <td key={year} className="cs-figure text-right px-4 py-3" style={{ color: 'var(--color-needs-human-text)' }}>
                  <FinancialValue value={nonOperatingByYear[year]} />
                </td>
              ))}
              <td className="cs-figure text-right px-4 py-3 text-lg" style={{ color: 'var(--color-needs-human-text)', background: 'var(--color-needs-human-tint)' }}>
                <FinancialValue value={totalNonOperating} />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="rounded-lg p-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          <p className="cs-label mb-1">Partners</p>
          <p className="cs-figure-lg">{partnerNames.length}</p>
        </div>
        <div className="rounded-lg p-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          <p className="cs-label mb-1">Years Covered</p>
          <p className="cs-figure-lg">{years.length}</p>
        </div>
        <div className="rounded-lg p-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          <p className="cs-label mb-1">Box 1 Total (Officer Comp)</p>
          <p className="cs-figure-lg"><FinancialValue value={grandBox1} /></p>
        </div>
        <div className="rounded-lg p-4" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
          <p className="cs-label mb-1" style={{ color: 'var(--color-needs-human-text)' }}>Non-Operating Income</p>
          <p className="cs-figure-lg" style={{ color: 'var(--color-needs-human-text)' }}><FinancialValue value={totalNonOperating} /></p>
        </div>
      </div>
    </div>
  );
}
